import { Form, notification } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { localize, saveResponseFile  } from 'mixins/helpers';
import {
    isCanceledRequest,
    useCancelableRequest,
} from 'mixins/hooks/useCancelableRequest';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getApiServiceDictionary,
    getTokenApiAccess,
    getTokenApiAccessCsv,
    saveTokenApiAccess,
} from '/react4xp/parts/admin-api-access/actions';
import {
    ApiTokenAccessItem,
    ApiServiceDictionaryItem,
} from '/react4xp/parts/admin-api-access/types';
import {
    DEFAULT_LIMIT_PER_DAY,
    DEFAULT_PAGE_SIZE,
} from '../utils/constants';
import {
    buildApiAccessRecords,
    getRequestErrorKey,
    sanitizeTaxpayerCode,
    serializeApiTokenAccessItems,
    toApiTokenAccessItems,
} from '../utils/helpers';
import { ApiAccessFilterValues } from '../utils/types';

export const useApiAccess = (translations: Record<string, string>) => {
    const [filterForm] = Form.useForm<ApiAccessFilterValues>();
    const currentTaxpayerCode = Form.useWatch('taxpayerCode', filterForm);
    const {
        abortRequest: abortAccessRequest,
        createRequestSignal: createAccessRequestSignal,
        finishRequest: finishAccessRequest,
    } = useCancelableRequest();
    const [accessItems, setAccessItems] = useState<ApiTokenAccessItem[]>([]);
    const [initialAccessItems, setInitialAccessItems] = useState<ApiTokenAccessItem[]>([]);
    const [dictionary, setDictionary] = useState<ApiServiceDictionaryItem[]>([]);
    const [searchedTaxpayerCode, setSearchedTaxpayerCode] = useState<string>();
    const [bulkLimit, setBulkLimit] = useState<number | null>(DEFAULT_LIMIT_PER_DAY);
    const [hasSearched, setHasSearched] = useState(false);
    const [isDictionaryLoading, setIsDictionaryLoading] = useState(false);
    const [isAccessLoading, setIsAccessLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    });
    const isSearchContextCurrent =
        hasSearched && currentTaxpayerCode === searchedTaxpayerCode;

    const accessRecords = useMemo(
        () => (hasSearched ? buildApiAccessRecords(dictionary, accessItems) : []),
        [accessItems, dictionary, hasSearched]
    );

    const initialAccessRecords = useMemo(
        () => (hasSearched ? buildApiAccessRecords(dictionary, initialAccessItems) : []),
        [dictionary, hasSearched, initialAccessItems]
    );

    const normalizedAccessItems = useMemo(
        () => toApiTokenAccessItems(accessRecords),
        [accessRecords]
    );

    const isDirty = useMemo(
        () =>
            hasSearched &&
            serializeApiTokenAccessItems(normalizedAccessItems) !==
            serializeApiTokenAccessItems(toApiTokenAccessItems(initialAccessRecords)),
        [hasSearched, initialAccessRecords, normalizedAccessItems]
    );

    const notifyRequestError = useCallback(
        (error: any, fallbackKey?: string) => {
            notification.error({
                message: localize(getRequestErrorKey(error, fallbackKey), translations),
            });
        },
        [translations]
    );

    const loadDictionary = useCallback(() => {
        setIsDictionaryLoading(true);

        getApiServiceDictionary()
            .then((response) => {
                setDictionary(response.data.filter((item) => item.isActive));
            })
            .catch((error) => notifyRequestError(error, 'admin-api-access-error-dictionary'))
            .finally(() => setIsDictionaryLoading(false));
    }, [notifyRequestError]);

    useEffect(() => {
        setPagination((current) => ({
            ...current,
            total: accessRecords.length,
        }));
    }, [accessRecords.length]);

    useEffect(() => {
        loadDictionary();
    }, [loadDictionary]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isDirty) {
                return undefined;
            }

            event.preventDefault();
            event.returnValue = '';

            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    const handleSearch = (values: ApiAccessFilterValues) => {
        const taxpayerCode = values.taxpayerCode;

        if (!taxpayerCode || isSaving) {
            return;
        }

        const signal = createAccessRequestSignal();

        setIsAccessLoading(true);

        getTokenApiAccess(taxpayerCode, signal)
            .then((response) => {
                const nextItems = response.data || [];

                setAccessItems(nextItems);
                setInitialAccessItems(nextItems);
                setSearchedTaxpayerCode(taxpayerCode);
                setHasSearched(true);
                setPagination((current) => ({
                    ...current,
                    current: 1,
                }));
            })
            .catch((error) => {
                if (isCanceledRequest(error)) {
                    return;
                }

                setAccessItems([]);
                setInitialAccessItems([]);
                setHasSearched(false);
                notifyRequestError(error, 'admin-api-access-error-load');
            })
            .finally(() => {
                if (finishAccessRequest(signal)) {
                    setIsAccessLoading(false);
                }
            });
    };

    const handleReset = () => {
        abortAccessRequest();
        filterForm.resetFields();
        setAccessItems([]);
        setInitialAccessItems([]);
        setSearchedTaxpayerCode(undefined);
        setHasSearched(false);
        setIsAccessLoading(false);
        setBulkLimit(DEFAULT_LIMIT_PER_DAY);
        setPagination((current) => ({
            ...current,
            current: 1,
            total: 0,
        }));
    };

    const handleFilterTaxpayerCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        filterForm.setFieldValue('taxpayerCode', sanitizeTaxpayerCode(event.target.value));
    };

    const handleTableChange = (nextPagination: TablePaginationConfig) => {
        setPagination((current) => ({
            ...current,
            current: nextPagination.current || 1,
            pageSize: nextPagination.pageSize || DEFAULT_PAGE_SIZE,
        }));
    };

    const updateAccessItem = useCallback((
        apiCode: string,
        patch: Partial<ApiTokenAccessItem>
    ) => {
        setAccessItems((current) => {
            const existingItem = current.find((item) => item.apiCode === apiCode);

            if (existingItem) {
                return current.map((item) =>
                    item.apiCode === apiCode
                        ? {
                            ...item,
                            ...patch,
                        }
                        : item
                );
            }

            return [
                ...current,
                {
                    apiCode,
                    isAllowed: true,
                    limitPerDay: null,
                    limitPerMinute: null,
                    ...patch,
                },
            ];
        });
    }, []);

    const handleAllowedChange = useCallback(
        (apiCode: string, isAllowed: boolean) => {
            updateAccessItem(apiCode, {
                isAllowed,
                limitPerDay: isAllowed ? DEFAULT_LIMIT_PER_DAY : null,
            });
        },
        [updateAccessItem]
    );

    const handleLimitChange = useCallback(
        (apiCode: string, limitPerDay: number | null) => {
            updateAccessItem(apiCode, { limitPerDay });
        },
        [updateAccessItem]
    );

    const handleMinuteLimitChange = useCallback(
        (apiCode: string, limitPerMinute: number | null) => {
            updateAccessItem(apiCode, { limitPerMinute });
        },
        [updateAccessItem]
    );

    const handleEnableAll = () => {
        setAccessItems(
            accessRecords.map((record) => ({
                apiCode: record.apiCode,
                isAllowed: true,
                limitPerDay: record.limitPerDay ?? DEFAULT_LIMIT_PER_DAY,
                limitPerMinute: record.limitPerMinute ?? null,
            }))
        );
    };

    const handleDisableAll = () => {
        setAccessItems(
            accessRecords.map((record) => ({
                apiCode: record.apiCode,
                isAllowed: false,
                limitPerDay: null,
                limitPerMinute: record.limitPerMinute ?? null,
            }))
        );
    };

    const handleApplyLimitToAll = () => {
        if (bulkLimit === null) {
            return;
        }

        setAccessItems(
            accessRecords.map((record) => ({
                apiCode: record.apiCode,
                isAllowed: record.isAllowed,
                limitPerDay: record.isAllowed ? bulkLimit : null,
                limitPerMinute: record.limitPerMinute ?? null,
            }))
        );
    };

    const handleSave = () => {
        if (!searchedTaxpayerCode || !isSearchContextCurrent) {
            notification.warning({
                message: localize('admin-api-access-search-first', translations),
            });
            return;
        }

        if (!normalizedAccessItems.length) {
            notification.warning({
                message: localize('admin-api-access-validation-items', translations),
            });
            return;
        }

        const hasInvalidItem = normalizedAccessItems.some((item) => {
            const isLimitInvalid =
                item.limitPerDay !== null &&
                (!Number.isFinite(Number(item.limitPerDay)) || Number(item.limitPerDay) < 0);

            const isMinuteLimitInvalid =
                item.limitPerMinute !== null &&
                (
                    !Number.isInteger(Number(item.limitPerMinute)) ||
                    Number(item.limitPerMinute) < -1
                );

            return (
                !item.apiCode ||
                typeof item.isAllowed !== 'boolean' ||
                isLimitInvalid ||
                isMinuteLimitInvalid
            );
        });

        if (hasInvalidItem) {
            notification.warning({
                message: localize('admin-api-access-validation-limit', translations),
            });
            return;
        }

        setIsSaving(true);

        saveTokenApiAccess({
            taxpayerCode: searchedTaxpayerCode,
            items: normalizedAccessItems,
        })
            .then(() => {
                setAccessItems(normalizedAccessItems);
                setInitialAccessItems(normalizedAccessItems);
                notification.success({
                    message: localize('admin-api-access-save-success', translations),
                });
            })
            .catch((error) => notifyRequestError(error, 'admin-api-access-error-save'))
            .finally(() => setIsSaving(false));
    };

    const handleExport = () => {
        if (!searchedTaxpayerCode || !isSearchContextCurrent) {
            notification.warning({
                message: localize('admin-api-access-search-first', translations),
            });
            return;
        }

        setIsExporting(true);

        getTokenApiAccessCsv(searchedTaxpayerCode)
            .then((response) => {
                saveResponseFile(response, `api-access-${searchedTaxpayerCode}.csv`);
            })
            .catch((error) => notifyRequestError(error, 'admin-api-access-error-export'))
            .finally(() => setIsExporting(false));
    };

    return {
        accessRecords,
        bulkLimit,
        dictionary,
        filterForm,
        handleApplyLimitToAll,
        handleAllowedChange,
        handleBulkLimitChange: setBulkLimit,
        handleDisableAll,
        handleEnableAll,
        handleExport,
        handleFilterTaxpayerCodeChange,
        handleLimitChange,
        handleMinuteLimitChange,
        handleReset,
        handleSave,
        handleSearch,
        handleTableChange,
        hasSearched,
        isDirty,
        isAccessLoading,
        isDictionaryLoading,
        isExporting,
        isSaving,
        isSearchContextCurrent,
        pagination,
    };
};